// Angular
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

// Third-party
import { ROUTES } from '@routes';
import { validate as isValidGuid } from 'uuid';

// Local
import { FILING_ROUTES } from '@feature/filing/filing-routes.constants';

/**
 * Shared guard logic to check for draftId and redirect if missing
 *
 * @param route The route being activated
 * @param redirectRoute The route to redirect to if draftId is missing
 * @returns true if draftId exists, or a UrlTree to redirect if missing
 */
const createFilingRedirectGuard = (redirectRoute: string): CanActivateFn => {
  return (route, state) => {
    void state;
    const router = inject(Router);

    // Draft id must come from route params (guards run before resolvers; FilingDataService is route-scoped).
    const draftIdFromRoute =
      route.paramMap.get('draftId') ?? route.parent?.paramMap.get('draftId');

    if (!draftIdFromRoute || !isValidGuid(draftIdFromRoute)) {
      return router.createUrlTree([ROUTES.FILING, redirectRoute]);
    }

    return true;
  };
};

/**
 * A guard to redirect to new case route if draftId is missing
 * @param route
 * @returns
 */
export const filingNewCaseRedirectGuard: CanActivateFn = (route, state) =>
  createFilingRedirectGuard(FILING_ROUTES.NEW_CASE)(route, state);

/**
 * A guard to redirect to existing case route if draftId is missing
 * @param route
 * @returns
 */
export const filingExistingCaseRedirectGuard: CanActivateFn = (route, state) =>
  createFilingRedirectGuard(FILING_ROUTES.EXISTING_CASE)(route, state);

/**
 * A guard to redirect to multi case route if draftId is missing
 * @param route
 * @returns
 */
export const filingMultiCaseRedirectGuard: CanActivateFn = (route, state) =>
  createFilingRedirectGuard(FILING_ROUTES.MULTI_CASE)(route, state);
